import { zodResolver } from '@hookform/resolvers/zod'
import { IconPencil } from '@tabler/icons-react'
import { useCallback, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton'
import { SecondaryButton } from '../../atoms/buttons/SecondaryButton'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../atoms/dialog'
import { Input } from '../../atoms/inputs/Input'
import { TextArea } from '../../atoms/inputs/InputTextArea'
import { Checkbox } from '../../atoms/buttons/CheckBoxButton'
import Typography from '../../atoms/Typography'
import CountryPicker from '../../molecules/CountryPicker'
import TenantAccessSelector from '../../molecules/TenantAccessSelector'
import DatasetSelector from '../DatasetSelector'
import { api } from 'src/api/root'
import { LayerItem, LayerType, UpdateLayerMetadataPayload } from 'src/types/layer'

const MAX_INTERNAL_NOTES = 5_000

const EditLayerSchema = z
  .object({
    datasetId: z.string().nullable(),
    country: z.string().nullable(),
    // Empty optional field stays null; only validate positivity once a value is entered.
    resolutionMeters: z.preprocess(
      (v) => (v === '' || v === null || v === undefined ? null : Number(v)),
      z.number().positive({ message: 'Resolution must be a positive number' }).nullable(),
    ),
    internalNotes: z
      .string()
      .trim()
      .max(MAX_INTERNAL_NOTES, { message: `Internal notes cannot exceed ${MAX_INTERNAL_NOTES} characters` })
      .nullable(),
    isPublic: z.boolean(),
    allowedTenantIds: z.array(z.string()).default([]),
  })
  .refine((d) => d.isPublic || d.allowedTenantIds.length > 0, {
    message: 'A private layer must grant access to at least one tenant',
    path: ['allowedTenantIds'],
  })

type EditLayerFormData = z.infer<typeof EditLayerSchema>

interface Props {
  layer: LayerItem
}

const EditLayerDialog = ({ layer }: Props) => {
  const [opened, setOpened] = useState(false)
  const { mutateAsync: updateLayer } = api.layer.updateMetadata.useMutation()

  const toDefaults = (l: LayerItem): EditLayerFormData => ({
    datasetId: l.datasetId,
    country: l.country,
    resolutionMeters: l.resolutionMeters,
    internalNotes: l.internalNotes,
    isPublic: l.isPublic,
    allowedTenantIds: l.allowedTenants.map((t) => t.tenantId),
  })

  const form = useForm<EditLayerFormData>({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: zodResolver(EditLayerSchema),
    defaultValues: toDefaults(layer),
  })

  useEffect(() => {
    if (opened) form.reset(toDefaults(layer))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, layer])

  const isPublic = form.watch('isPublic')
  const isVector = layer.type === LayerType.VECTOR
  const notesLength = form.watch('internalNotes')?.length ?? 0

  const handleSubmit: SubmitHandler<EditLayerFormData> = useCallback(
    async (data) => {
      const dirtyFields = form.formState.dirtyFields
      // dataset/country/tenants are set via setValue (not register), so compare with the layer directly.
      const initialTenantIds = layer.allowedTenants.map((t) => t.tenantId)
      const tenantsChanged =
        data.allowedTenantIds.length !== initialTenantIds.length ||
        data.allowedTenantIds.some((id) => !initialTenantIds.includes(id))

      const payload: UpdateLayerMetadataPayload = {
        ...(data.datasetId !== layer.datasetId && { datasetId: data.datasetId }),
        ...(data.country !== layer.country && { country: data.country }),
        ...(dirtyFields.resolutionMeters && { resolutionMeters: isVector ? null : data.resolutionMeters }),
        ...(dirtyFields.internalNotes && { internalNotes: data.internalNotes?.trim() || null }),
        // isPublic and the tenant list are coupled — send both together so the backend
        // access guard always validates a consistent pair.
        ...((data.isPublic !== layer.isPublic || tenantsChanged) && {
          isPublic: data.isPublic,
          allowedTenantIds: data.isPublic ? [] : data.allowedTenantIds,
        }),
      }

      if (Object.keys(payload).length > 0) {
        await updateLayer({ fileId: layer.id, data: payload })
      }
      setOpened(false)
    },
    [updateLayer, layer, isVector, form],
  )

  return (
    <Dialog open={opened} onOpenChange={setOpened}>
      <DialogTrigger className="flex h-11 w-11 items-center justify-center rounded-[8px] text-light-green-500 transition-all hover:cursor-pointer hover:bg-light-green-500 hover:text-white active:scale-95">
        <IconPencil size={24} />
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[640px]" aria-description="edit-layer-dialog">
        <DialogHeader aria-describedby="edit-layer-dialog">
          <DialogTitle>
            <Typography variant="2xl-bold">Edit Layer</Typography>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <DatasetSelector
              label="Dataset"
              defaultValue={layer.dataset}
              onDatasetChange={(datasetId) => form.setValue('datasetId', datasetId, { shouldValidate: true })}
              placeholder="Search or create dataset"
            />
            <Input
              type="number"
              step="any"
              autoComplete="off"
              disabled={isVector}
              label={`Resolution (meters)${isVector ? ' (raster only)' : ''}`}
              placeholder={isVector ? 'Not applicable to vector layers' : 'e.g. 10'}
              errorMessage={form.formState.errors.resolutionMeters?.message}
              {...form.register('resolutionMeters')}
            />
          </div>

          <div className="border-t border-gray-700 pt-4">
            <CountryPicker
              label="Country"
              value={form.watch('country')}
              onChange={(country) => form.setValue('country', country, { shouldValidate: true })}
            />
          </div>

          <div className="flex flex-col gap-4 border-t border-gray-700 pt-4">
            <Checkbox
              id="isPublic"
              checked={isPublic}
              onCheckedChange={(checked) => form.setValue('isPublic', checked, { shouldValidate: true })}
              label="Public — visible to all tenants"
            />
            {!isPublic && (
              <div className="flex flex-col gap-2">
                <TenantAccessSelector
                  selectedTenantIds={form.watch('allowedTenantIds')}
                  onChange={(ids) => form.setValue('allowedTenantIds', ids, { shouldValidate: true })}
                  currentTenantIds={layer.allowedTenants.map((t) => t.tenantId)}
                  enabled={opened}
                />
                {form.formState.errors.allowedTenantIds?.message && (
                  <Typography variant="sm" className="text-red-ramo">
                    {form.formState.errors.allowedTenantIds.message}
                  </Typography>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-gray-700 pt-4">
            <TextArea
              rows={5}
              maxLength={MAX_INTERNAL_NOTES}
              label="Internal notes (COPA only — not shown to users)"
              placeholder="Guidance for layer selection"
              errorMessage={form.formState.errors.internalNotes?.message}
              {...form.register('internalNotes')}
            />
            <span
              className={`mt-1 block text-right text-xs ${
                notesLength >= MAX_INTERNAL_NOTES ? 'text-red-400' : 'text-gray-500'
              }`}
            >
              {notesLength}/{MAX_INTERNAL_NOTES}
            </span>
          </div>
        </div>

        <DialogFooter className="sm:justify-center sm:space-x-6">
          <SecondaryButton isLoading={form.formState.isSubmitting} onClick={() => setOpened(false)} className="w-40">
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={form.handleSubmit(handleSubmit)}
            isLoading={form.formState.isSubmitting}
            className="w-40"
          >
            Save
          </PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default EditLayerDialog
